import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AlertDialog from 'src/components/AlertDialog';
import { invalidateBillingTags, useInvoiceDetailsQuery } from 'src/pages/billing/billingApi';
import {
  invalidateSingleStudentTags,
  useSingleStudentDetailsQuery,
} from 'src/pages/single-student/singleStudentApi';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';
import { apiErrorsToDict } from 'src/types/api-error-response';

import { useDeleteSessionMutation } from '../scheduleApi';

export const useDeleteSession = () => {
  const [sessionId, setSessionId] = useState<string>();

  const onDeleteSession = useCallback((id: string) => {
    setSessionId(id);
  }, []);

  const onCancelDeleteSession = useCallback(() => {
    setSessionId(undefined);
  }, []);

  return {
    onDeleteSession,
    onCancelDeleteSession,
    sessionId,
  };
};

type DeleteSessionAlertDialogProps = {
  sessionId?: string;
  onCancel: () => void;
  onSuccessCb?: () => void;
};

const DeleteSessionAlertDialog = ({
  sessionId,
  onCancel,
  onSuccessCb,
}: DeleteSessionAlertDialogProps) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [deleteSession, { isLoading, isError, isSuccess, error }] = useDeleteSessionMutation();
  const location = useLocation();
  const isStudentProfile = location.pathname.includes('student');
  const { data: invoiceData, isFetching } = useInvoiceDetailsQuery(id!, {
    skip: !id || isStudentProfile,
  });
  const studentId = invoiceData?.studentId || id;

  const { refetch, isUninitialized } = useSingleStudentDetailsQuery(studentId!, {
    skip: !studentId || isFetching,
  });

  useEffect(() => {
    if (isError && error) {
      const is400 = 'status' in error && error.status === 400;
      const errorsDict = apiErrorsToDict(error);
      const errorMessage =
        is400 && errorsDict?._ ? errorsDict._ : 'Something went wrong. Try again later.';
      dispatch(addToast({ text: errorMessage, type: 'negative' }));
      onCancel();
    }
  }, [dispatch, isError, error, onCancel]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(addToast({ text: 'Session has been successfully deleted.', type: 'positive' }));
      dispatch(invalidateBillingTags(['InvoiceDetails']));
      dispatch(invalidateSingleStudentTags(['StudentEvents']));
      onCancel();
      onSuccessCb?.();
    }
  }, [dispatch, isSuccess, onCancel, onSuccessCb]);

  const onDelete = async () => {
    if (sessionId) {
      await deleteSession(sessionId);
      if (!isUninitialized) {
        refetch();
      }
    }
  };

  return (
    <AlertDialog
      open={!!sessionId}
      onCancelClick={onCancel}
      onActionClick={onDelete}
      title="Are you sure you want to delete this session?"
      description="Deleted sessions cannot be restored."
      actionButtonLabel="Delete session"
      isActionLoading={isLoading}
    />
  );
};

export default DeleteSessionAlertDialog;
